import { cosmeticDef, gachaPool, type CosmeticDef } from '../data/cosmetics';
import { FORTUNE_RULES, FORTUNE_SAYINGS, FORTUNE_TIERS, type FortuneTier } from '../data/fortune';
import { t } from '../../i18n';
import { dayKey } from './daily';
import type { SaveData } from './model';
import { addMemory } from './progression';

/** Random number in [0, 1); injectable so tests can seed it. */
export type Rng = () => number;

export const TIER_ORDER: FortuneTier[] = ['common', 'rare', 'epic', 'legendary'];
const atLeast = (tier: FortuneTier, min: FortuneTier): FortuneTier => (TIER_ORDER.indexOf(tier) < TIER_ORDER.indexOf(min) ? min : tier);

/** Adds tickets up to the cap; returns how many were actually added. */
export function addTickets(save: SaveData, n: number): number {
  const before = save.fortune.tickets;
  save.fortune.tickets = Math.min(FORTUNE_RULES.ticketCap, before + n);
  return save.fortune.tickets - before;
}

/** Today's free ticket has not been picked up yet (and there is room for it). */
export function freeTicketWaiting(save: SaveData, now = new Date()): boolean {
  return save.fortune.lastFreeDay !== dayKey(now) && save.fortune.tickets < FORTUNE_RULES.ticketCap;
}

/**
 * Gives today's free ticket, once per calendar day. With a full ticket pocket
 * the day is not used up, so the ticket can still be picked up after a draw.
 */
export function claimFreeTicket(save: SaveData, now = new Date()): boolean {
  if (!freeTicketWaiting(save, now)) return false;
  save.fortune.lastFreeDay = dayKey(now);
  return addTickets(save, 1) > 0;
}

/** Coin draws still allowed today. */
export function coinDrawsLeft(save: SaveData, now = new Date()): number {
  const used = save.fortune.coinDay === dayKey(now) ? save.fortune.coinDraws : 0;
  return Math.max(0, FORTUNE_RULES.dailyCoinDraws - used);
}

/** Prizes of a tier the player can still win (never owned ones: no duplicates). */
export function prizePool(save: SaveData, tier: FortuneTier): CosmeticDef[] {
  return gachaPool(tier).filter((c) => !save.cosmetics.owned.includes(c.id));
}

export interface FortuneResult {
  tier: FortuneTier;
  /** Coins paid instead of an item (everything of the tier is owned already). */
  coins: number;
  /** Cosmetic id won; null when the tier was complete and coins were paid. */
  item: string | null;
  /** English source of the funny fortune (translate with t()). */
  saying: string;
  /** A guarantee lifted this draw to its tier. */
  pity: boolean;
}

const pick = <T>(list: T[], rng: Rng): T => list[Math.min(list.length - 1, Math.floor(rng() * list.length))]!;

/** Rolls a draw without changing the save. */
export function rollFortune(save: SaveData, rng: Rng = Math.random): FortuneResult {
  let roll = rng() * 100;
  let tier: FortuneTier = FORTUNE_TIERS[FORTUNE_TIERS.length - 1]!.tier;
  for (const def of FORTUNE_TIERS) {
    if (roll < def.percent) {
      tier = def.tier;
      break;
    }
    roll -= def.percent;
  }
  const f = save.fortune;
  const { pity } = FORTUNE_RULES;
  const guaranteed: FortuneTier | null =
    f.sinceLegendary + 1 >= pity.legendary ? 'legendary' : f.sinceEpic + 1 >= pity.epic ? 'epic' : f.sinceRare + 1 >= pity.rare ? 'rare' : null;
  const rolled = tier;
  if (guaranteed) tier = atLeast(tier, guaranteed);

  const pool = prizePool(save, tier);
  const item = pool.length ? pick(pool, rng).id : null;
  return { tier, coins: item ? 0 : FORTUNE_RULES.refund[tier], item, saying: pick(FORTUNE_SAYINGS, rng), pity: tier !== rolled };
}

/** Pays one draw's prize and moves the guarantee counters. */
function applyDraw(save: SaveData, r: FortuneResult): void {
  const f = save.fortune;
  f.draws += 1;
  const i = TIER_ORDER.indexOf(r.tier);
  f.sinceRare = i >= 1 ? 0 : f.sinceRare + 1;
  f.sinceEpic = i >= 2 ? 0 : f.sinceEpic + 1;
  f.sinceLegendary = i >= 3 ? 0 : f.sinceLegendary + 1;
  save.coins += r.coins;
  const def = r.item ? cosmeticDef(r.item) : undefined;
  if (!def) return;
  save.cosmetics.owned.push(def.id);
  // A new victory stamp goes on straight away, like a bought one.
  if (def.kind === 'stamp') save.cosmetics.stamp = def.id;
  if (r.tier === 'epic' || r.tier === 'legendary') {
    addMemory(save, { kind: 'item', title: t('Fortune teller prize: {item}', { item: t(def.name) }), text: t(r.saying), sticker: r.tier === 'legendary' ? 'crown' : 'star' });
  }
}

export type Payment = 'ticket' | 'coins';

/** How the next single draw would be paid (a ticket first), or null when it cannot be paid. */
export function nextPayment(save: SaveData, now = new Date()): Payment | null {
  if (save.fortune.tickets > 0) return 'ticket';
  if (save.coins >= FORTUNE_RULES.drawCost && coinDrawsLeft(save, now) > 0) return 'coins';
  return null;
}

function countCoinDraws(save: SaveData, n: number, now: Date): void {
  const day = dayKey(now);
  if (save.fortune.coinDay !== day) {
    save.fortune.coinDay = day;
    save.fortune.coinDraws = 0;
  }
  save.fortune.coinDraws += n;
}

/** Spends a ticket (or, without one, the coins) and pays the prize. Null when it cannot be paid. */
export function drawFortune(save: SaveData, rng: Rng = Math.random, now = new Date()): FortuneResult | null {
  const pay = nextPayment(save, now);
  if (!pay) return null;
  const r = rollFortune(save, rng);
  if (pay === 'ticket') save.fortune.tickets -= 1;
  else {
    save.coins -= FORTUNE_RULES.drawCost;
    countCoinDraws(save, 1, now);
  }
  applyDraw(save, r);
  return r;
}

/** Five draws for FORTUNE_RULES.fiveCost coins. Null when the coins or today's draws run short. */
export function drawFive(save: SaveData, rng: Rng = Math.random, now = new Date()): FortuneResult[] | null {
  if (save.coins < FORTUNE_RULES.fiveCost || coinDrawsLeft(save, now) < 5) return null;
  save.coins -= FORTUNE_RULES.fiveCost;
  countCoinDraws(save, 5, now);
  const out: FortuneResult[] = [];
  for (let i = 0; i < 5; i++) {
    const r = rollFortune(save, rng);
    applyDraw(save, r);
    out.push(r);
  }
  return out;
}

/** How much of the fortune teller's prize list the player owns. */
export function collection(save: SaveData): { owned: number; total: number } {
  const all = TIER_ORDER.flatMap((tier) => gachaPool(tier));
  return { owned: all.filter((c) => save.cosmetics.owned.includes(c.id)).length, total: all.length };
}

/** A colour word letter by letter, as it is spelled out: Thai vowel and tone marks stay on their letter. */
export function spellLetters(word: string): string[] {
  return word.replace(/\s/g, '').match(/.[ัิ-ฺ็-๎]*/gu) ?? [];
}
